/**
 * finalizar-helpers.ts — Pure helpers extracted from InformeEditor.finalizar().
 *
 * Extracted to fix W1: the stale React-state closure bug where finalizar() called
 * guardar() on the online path, causing guardar() to re-read the stale `ac` state
 * (still holding raw data: URLs) and upload firma blobs a SECOND time to R2.
 *
 * These helpers:
 *   - queueFirmaBlobs: converts AC firma data: URLs to IDB blobs + idb-blob: placeholders.
 *   - savePayloadToNetwork: PATCHes the informe payload with an EXPLICIT payload object,
 *     bypassing the React state closure entirely.
 */
import { enqueueBlob, drainBlobs, discardBlobs } from "@/features/offline-sync/services/blob-queue.js";
import {
  containsBlobPlaceholders,
  getUploadedBlobUrlMap,
  injectBlobUrls,
} from "@/features/offline-sync/services/flush.js";
import { remove as removeFromOutbox } from "@/features/offline-sync/services/outbox.js";
import { changeEstadoInforme, updateInformePayload } from "../services/reports.service.js";
import type { AcPayload } from "../types.js";

/**
 * Converts AC firma data: URLs into IDB-queued blobs and returns the AcPayload
 * with `idb-blob:` placeholders replacing any raw data: URLs.
 *
 * Idempotent for non-data: values: http URLs, empty strings, and existing
 * idb-blob: placeholders are passed through unchanged.
 */
export async function queueFirmaBlobs(
  ac: AcPayload,
  localId: string,
): Promise<AcPayload> {
  async function queueSingleFirma(fieldPath: string, value: string): Promise<string> {
    if (!value || !value.startsWith("data:")) return value;
    const raw = await (await fetch(value)).blob();
    const blobId = `${localId}:${fieldPath}`;
    await enqueueBlob({
      id: blobId,
      ownerLocalId: localId,
      fieldPath,
      blob: raw,
      contentType: "image/png",
    });
    // ponytail: placeholder replaced by flush with real R2 URL on reconnect.
    return `idb-blob:${blobId}`;
  }

  const tecnico = await queueSingleFirma("firmas.tecnico", ac.firmas.tecnico);
  const cliente = ac.firmas.cliente
    ? await queueSingleFirma("firmas.cliente", ac.firmas.cliente)
    : undefined;

  return {
    ...ac,
    firmas: cliente ? { tecnico, cliente } : { tecnico },
  };
}

/**
 * PATCHes the informe with an EXPLICIT payload, bypassing React state closures.
 *
 * This is the single network write on the online finalizar path. It does NOT
 * call uploadImagen or subirFirma — the payload already contains idb-blob:
 * placeholders (queued by queueFirmaBlobs). Flush handles R2 upload on reconnect.
 */
export async function savePayloadToNetwork(
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await updateInformePayload(id, payload);
}

/**
 * Drains ALL queued blobs for the report (firmas AND photos, one machinery) and
 * resolves `idb-blob:` placeholders in the payload to real R2 URLs, BEFORE the
 * online finalize PATCH.
 *
 * - Full drain  → { payload with real URLs, fullyResolved: true }. The caller
 *   may PATCH and, after a successful sync, discardBlobs.
 * - Partial drain → { original payload (placeholders kept), fullyResolved:
 *   false }. The caller MUST NOT PATCH, MUST NOT discardBlobs, and leaves the
 *   report en_cola — the flush completes the sync later.
 *
 * Post-injection assertion (remediation): a vacuous drain (nothing pending in
 * IDB) does NOT imply resolution — a stale local draft can still carry
 * `idb-blob:` placeholders whose blobs were already discarded. If the injected
 * payload STILL contains any placeholder, fullyResolved is false: no PATCH, no
 * discardBlobs, report stays en_cola. The server must never store a placeholder.
 *
 * Blobs are NEVER deleted here: deletion is only safe after a full drain AND a
 * successful sync (finalizar() owns that decision).
 */
export async function resolvePayloadBlobs(
  localId: string,
  payload: Record<string, unknown>,
): Promise<{ payload: Record<string, unknown>; fullyResolved: boolean }> {
  const drained = await drainBlobs(localId);
  if (!drained) return { payload, fullyResolved: false };
  const urlMap = await getUploadedBlobUrlMap(localId);
  const resolved = injectBlobUrls(payload, urlMap) as Record<string, unknown>;
  if (containsBlobPlaceholders(resolved)) {
    return { payload, fullyResolved: false };
  }
  return { payload: resolved, fullyResolved: true };
}

/**
 * The online finalize sync step, extracted from InformeEditor.finalizar() so
 * the branch is integration-testable without mounting the editor.
 *
 * - drain not fully resolved → returns false WITHOUT any network write and
 *   WITHOUT discardBlobs: the report stays en_cola with its blobs queued and
 *   placeholders intact; flush resolves and syncs them on reconnect.
 * - fully resolved → PATCHes the resolved payload, transitions borrador →
 *   en_cola → sincronizado, then drops the optimistic outbox entry and
 *   discards the drained blobs. If the sincronizado transition fails, the
 *   outbox entry and blobs are KEPT (flush completes the sync later).
 *
 * Returns true when the payload was PATCHed (even if sincronizado failed),
 * false when nothing was sent (caller shows the "quedó en cola" toast).
 */
export async function syncFinalizedReport(
  networkId: string,
  localId: string,
  drain: { payload: Record<string, unknown>; fullyResolved: boolean },
): Promise<boolean> {
  if (!drain.fullyResolved) return false;
  await savePayloadToNetwork(networkId, drain.payload);
  await changeEstadoInforme(networkId, "en_cola");
  try {
    await changeEstadoInforme(networkId, "sincronizado");
    // Fully synced with resolved data → drop the optimistic outbox entry (else
    // the next flush re-POSTs it with a stale timestamp and fires the misleading
    // "versión más nueva" toast) and discard the drained blobs — every blob was
    // uploaded and synced, so leaving them would orphan them in IDB.
    await removeFromOutbox(localId);
    await discardBlobs(localId);
  } catch {
    // queda en_cola; el flush completa el sincronizado (y sube las firmas) al reconectar.
  }
  return true;
}
